import { Injectable } from "@angular/core";
import { HttpCemex, CmxHttpOptions } from "@cemex-core/angular-services-v8";
import { EndpointService } from "./../../services/helpers/endpoint.service";
import { Observable, throwError } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { PayerBrokerRequest, PayerBrokerRequestV1 } from "./../models/payer-broker-request";
import { PayerBrokerResponse, PayerBrokerV1Response, PayerBrokerV3Response } from "./../models/payer-broker-response";
import { PaymentRequest } from "./../models/payment-request";
import * as moment from "moment";

@Injectable()
export class PaymentService {
  constructor(protected http: HttpCemex, protected endpointService: EndpointService) {}

  createReferenceV2(request: any): Observable<any> {
    const endpoint = this.endpointService.getUrl("v2", "pe", "referencepayment");
    const auxRequest = {
      header: [request.header],
      detail: request.detail,
    };

    return this.http.post(endpoint, auxRequest).pipe(
      map((response: Response) => {
        const reference = response;
        // const headers = reference.headerOut[0];
        return reference;
      }),
      catchError((error: any) => throwError("POST Request from Server error"))
    );
  }

  createReferenceV3(request: any): Observable<any> {
    const endpoint = this.endpointService.getUrl("v3", "pe", "references");

    return this.http.post(endpoint, request).pipe(
      map((response: any) => {
        const reference = response;
        const headers = reference.header;
        return headers;
      }),
      catchError((error: any) => throwError("POST Request from Server error"))
    );
  }

  createReferenceBRMv1(request: any): Observable<any> {
    const endpoint = this.endpointService.getUrl("v1", "brm", "references");
    const requestWrapped = this.wrapReferenceRequest(request);

    return this.http.post(endpoint, requestWrapped).pipe(
      map((response: Response) => {
        return response;
      }),
      catchError((error: any) => throwError("POST Request from Server error"))
    );
  }

  getPayerInformation(request: PayerBrokerRequest): Observable<any> {
    const endpoint = this.endpointService.getUrl("v1", "pe", "payerinformation");

    const options = {
      params: {
          amountDue: request?.amount ?? "",
          dueDate: moment(new Date()).format("YYYY-MM-DD"),
          referenceNumber: request?.referenceCode ?? "",
          customerCode: request?.customer?.customerCode ?? "",
          paymentMethod: request?.paymentType?.paymentTypeCode ?? "",
      },
    } as CmxHttpOptions;

    return this.http.get(endpoint, options).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error: any) => throwError("GET Request from Server error"))
    );
  }

  getPayerBroker(
    request: PayerBrokerRequest | any,
    brokerCode: string,
    capability = "brm"
  ): Observable<PayerBrokerResponse> {
    const endpoint = this.endpointService.getUrl("v1", capability, "payers" + "/" + brokerCode);

    return this.http.post(endpoint, request).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error: any) => throwError("POST Request from Server error"))
    );
  }

  getPayerBrokerV1(request: PayerBrokerRequestV1, brokerCode: string): Observable<PayerBrokerResponse> {
    const endpoint = this.endpointService.getUrl("v1", "brm", brokerCode + "/" + "payers");
    const requestWrapped = request;
    return this.http.post(endpoint, requestWrapped).pipe(
      map((response: any) => {
        let data = response;
        data.urlRedirect = data.urlRedirect + data.jwt;
        return data;
      }),
      catchError((error: any) => throwError("POST Request from Server error"))
    );
  }

  getPayerBrokerV2(request: PayerBrokerRequest, brokerCode: string): Observable<PayerBrokerResponse> {
    const endpoint = this.endpointService.getUrl("v2", "brm", "payers" + "/" + brokerCode);
    const requestWrapped = this.wrapPayerBrokerRequest(request);

    return this.http.post(endpoint, requestWrapped).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error: any) => throwError("POST Request from Server error"))
    );
  }

  getPayerBrokerV3(request: PayerBrokerRequestV1, brokerCode: string): Observable<PayerBrokerResponse> {
    const endpoint = this.endpointService.getUrl("v1", "brm", brokerCode + "/" + "payers");
    const requestWrapped = request;
    return this.http.post(endpoint, requestWrapped).pipe(
      map((response: any) => {
        let data = response;
        return data;
      }),
      catchError((error: any) => throwError("POST Request from Server error"))
    );
  }

  saveExternalOrderRelation(
    externalOrder: string,
    reference: string,
    bankCode: string,
    countryCode: string,
    customerCode: string,
    companyCode: string
  ): Observable<any> {
    const request = {
      header: {
        companyCode: companyCode,
        bankCode: bankCode,
        externalCode: externalOrder,
        countryCode: countryCode,
        customerCode: customerCode,
      },
    };
    const url = this.endpointService.getUrl("v3", "pe", "references" + "/" + reference);

    return this.http.patch(url, request).pipe(map((response: any) => response));
  }

  getLastMethodPayment(countryCode: string): Observable<any> {
    const endpoint = this.endpointService.getUrl("v2", "rc", "lastmethodpayment");

    const options = {
      params: {
          countryCode: countryCode ?? "",
      },
    } as CmxHttpOptions;

    return this.http.get(endpoint, options).pipe(
      map((response: Response) => {
        return response;
      })
    );
  }

  private wrapReferenceRequest(request: PaymentRequest): any {
    const requestWrapped = { header: {}, detail: [] };
    const header = {
      country: { countryCode: request.header.countryCompany },
      company: { companyCode: request.header.companyCode },
      customer: { customerCode: request.header.customerNo1 },
      bank: { bankCode: request.header.shortKey4HouseBank },
      payment: {
        paymentAmount: request.header.amountLocalCurrency,
        status: { statusCode: request.header.statusTransaction },
        method: { methodCode: request.header.paymentMethod },
        currency: { currencyCode: request.header.currencyKey },
        datePayment: request.header.datePay,
        timePayment: request.header.timePay,
      },
      jobsite: { jobsiteCode: request.header.shitpTo },
      invoicePay: request.header.invoicePay,
      cashSalesIndicator: request.header.cashSalesIndicator,
      sourceSystem: request.header.sourceSystem,
    };

    requestWrapped.header = header;

    if (request.detail && request.detail.length > 0) {
      const details = [];

      request.detail.forEach((detail) => {
        details.push({
          country: { countryCode: detail.countryCompany },
          company: { companyCode: detail.companyCode },
          reference: { referenceNumber: detail.referenceCode },
          customer: { customerCode: detail.customerNo1 },
          jobsite: { jobsiteCode: detail.shitpTo },
          document: {
            documentId: detail.documentID,
            documentCode: detail.documentCode,
            debitFlag: detail.debitFlag,
            fiscalYear: detail.fiscalYear,
            documentItem: detail.shortKey4HouseBank,
          },
          payment: {
            paymentAmount: detail.amountLocalCurrency,
            partialPayment: detail.paymentFlagPartialPayment,
          },
          reasonCode: detail.reasonCodeForPayments,
        });
      });

      requestWrapped.detail = details;
    }

    return requestWrapped;
  }

  private wrapPayerBrokerRequest(request: PayerBrokerRequest): any {
    return {
      reference: {
        referenceNumber: request.referenceCode,
      },
      urlRedirect: request.urlRedirect,
      country: request.country,
      company: request.company,
      payment: {
        paymentAmount: request.amount,
        method: {
          methodCode: request.paymentType.paymentTypeCode,
        },
      },
      customer: request.customer,
      user: request.user,
    };
  }
}
